import { Component, Input, OnDestroy, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { environment } from '@env/environment';
import { FileUploadService } from '@shared/services/file-upload.service';
import { UploadFile, UploadFilter, UploadXHRArgs } from 'ng-zorro-antd';
import { Subject, Observable, Observer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MessagesService, NotificationType } from '@shared/services/message.service';
import { HttpEvent, HttpEventType, HttpRequest, HttpResponse } from '@angular/common/http';
import { AutonomousService } from 'app/modules/autonomous/autonomous.service';

@Component({
  selector: 'app-file-upload',
  styleUrls: ['./file-upload.component.scss'],
  template: `
    <div *ngIf="!hideButton">
        <span  class="required">*</span>
        <nz-upload
                [nzFileList]="fileList"
                [nzRemove]="removeDocument"
                (nzChange)="handleChange($event)"
                [nzAccept]="getAcceptFiles()"
                [nzCustomRequest]="customReq"
                [nzFilter]="filters"
                [nzBeforeUpload]="beforeUpload"
        >
            <button style="margin-bottom: 15px" nz-button type="button" [disabled]="disableButton"
            nzTitle="{{help}}" nzPlacement="right" nz-tooltip>
                <i nz-icon type="upload"></i>
                <span>{{text}}</span>
            </button>
            <button style="margin-bottom: 15px" nz-button type="button" [disabled]="disableButton"
            nzTitle="{{help}}" nzPlacement="right" nz-tooltip *ngIf="hideButtonBoleto">
                <i nz-icon type="upload"></i>
                <span>{{text2}}</span>
            </button>
        </nz-upload>
    </div>
  `,
})
export class FileUploadComponent implements OnDestroy, OnChanges {
  @Input() attachmentType: string;

  @Input() hideButton = false;

  @Input() hideButtonBoleto = false;

  @Input() disableButton = false;

  @Input() text = 'Anexar Arquivos';

  @Input() text2 = 'Boleto ou Comprovante de Recolhimento'

  @Input() help;

  @Input() customRemove: any;

  @Input() onlyOneAttachment = false;

  fileList: any[];

  acceptFiles = ['pdf', 'png', 'jpg', 'jpeg', 'pgm']

  private unsubscribe$ = new Subject();
  private MAX_NAME_SIZE = 200;
  private MAX_FILE_SIZE = 1500;

  constructor(private fileUploadService: FileUploadService, private msgService: MessagesService,
    private autonomousService: AutonomousService) { }

  beforeUpload = (file: File) => {
    return new Observable((observer: Observer<boolean>) => {
      const fileNameSize = file.name.length;
      const isAllowedNameLength = fileNameSize < this.MAX_NAME_SIZE;
      if (!isAllowedNameLength) {
        this.msgService.notify('Permitido somente 200 caracteres no nome do anexo', NotificationType.Error);
        observer.complete();
        return;
      }

      const isAllowedSize = (file.size / 1024) < this.MAX_FILE_SIZE;
      if (!isAllowedSize) {
        this.msgService.notify('Permitido somente arquivos de até 1.5MB', NotificationType.Error);
        observer.complete();
        return;
      }

      if (this.onlyOneAttachment) {
        if (this.fileList.length != 0) {
          this.msgService.notify('Permitido somente um anexo', NotificationType.Error);
          observer.complete();
          return;
        }
      }
      const canUpload = this.checkIfFormIsNotPending();
      if (!canUpload) {
        this.msgService.notify('Não permitido a inserção de anexos enquanto o cadastro estiver pendente', NotificationType.Error);
        observer.complete();
        return;
      }

      observer.next(isAllowedSize);
      observer.complete();
    });
  };

  ngOnChanges(changes: SimpleChanges): void {
    const { attachmentType } = changes;
    if (!attachmentType || !attachmentType.currentValue) return;
    if (attachmentType.currentValue.search(null) < 0) {
      if (this.attachmentType.toString().indexOf('seq') < 0 && this.attachmentType.toString().indexOf('inss') < 0) {
        let att = this.fileUploadService.getAll();
        if (att) {
          let files = att.filter(at => at.tipDoc == this.attachmentType);
          files.forEach(element => {
            element.url = environment.api + element.path;
          })
          this.fileList = files;
        } else {
          this.fileList = [];
        }
      } else {
        this.fileUploadService
          .get(this.attachmentType)
          .pipe(
            takeUntil(this.unsubscribe$),
            map(response => {
              response.forEach(element => {
                element.url = environment.api + element.path;
              });
              return response;
            }),
          )
          .subscribe(files => (this.fileList = files));
      }
    } else {
      this.fileList = [];
    }
  }

  filters: UploadFilter[] = [
    {
      name: 'type',
      fn: (fileList: UploadFile[]) => {
        //Verifica se o type do arquivo contém em algum elemento do acceptFiles
        const filterFiles = fileList.filter(({ type }) => this.acceptFiles.find(allowed => type.indexOf(allowed) !== -1));
        if (filterFiles.length !== fileList.length) {
          this.msgService.notify(`Por favor insira um arquivo dentro dos formatos válidos (${this.acceptFiles.toString()})`, NotificationType.Error);
          return filterFiles;
        }
        return fileList;
      }
    }
  ];

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  apiUrl(): string {
    return `files/${this.attachmentType}`;
  }

  getAcceptFiles(): string {
    return this.acceptFiles.map(fil => '.' + fil).toString()
  }

  handleChange(event: any) {
    if (event.type === 'success') {
      let newFile = null;
      if (this.attachmentType.indexOf('seq') >= 0) {
        newFile = { ...event.file, url: `${environment.api}files/${this.attachmentType}/${event.file.response}` }
      } else {
        newFile = { ...event.file, url: `${environment.api}files/${event.file.response.res}`, tipDoc: this.attachmentType }
        newFile.response = event.file.response.res;
        this.fileUploadService.saveOne(newFile);
      }

      this.fileList = [...this.fileList, newFile]
    } else if (event.type === 'error') {
      this.fileList = this.fileList.filter(item => (item.id || item.response) !== event.file.id);
    }
  }

  customReq = (item: UploadXHRArgs) => {
    const formData = new FormData();
    let name = item.file.name;
    let file = new File([item.file as any], name, { type: item.file.type });
    formData.append('file', file as any);
    const req = new HttpRequest('POST', this.apiUrl(), formData, {
      reportProgress: true
    });
    return this.fileUploadService.post(req).subscribe(
      (event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total! > 0) {
            (event as any).percent = (event.loaded / event.total!) * 100;
          }
          item.onProgress!(event, item.file!);
        } else if (event instanceof HttpResponse) {
          item.onSuccess!(event.body, item.file!, event);
        }
      },
      err => {
        item.onError!(err, item.file!);
      }
    );
  };

  removeDocument = (file: UploadFile) => {
    let canDelete = this.checkIfFormIsNotPending();
    if (!canDelete) {
      this.msgService.notify('Não permitido a remoção de anexos enquanto o cadastro estiver pendente', NotificationType.Error);
      return;
    }
    let fileId = file.response ? file.response : file.id;
    if (fileId.res) {
      fileId = fileId.res;
    }
    if (this.customRemove) {
      this.fileUploadService
        .deleteInss(this.customRemove)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.fileUploadService.remove(this.attachmentType, fileId);
          this.fileList = this.fileList.filter(item => (item.id || item.response) !== fileId);
        });
    } else {
      this.fileUploadService
        .delete(this.attachmentType, fileId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          this.fileUploadService.remove(this.attachmentType, fileId);
          this.fileList = this.fileList.filter(item => (item.id || item.response) !== fileId);
        });
    }

  };

  private checkIfFormIsNotPending() {
    return !this.autonomousService.isFormPending();
  }
}
